import {useDataTable} from '@/hooks/useDataTable-hook';
import {useStableListQueryData} from '@/hooks/useStableListQueryData';
import {GetFormattedPerfectStoreData} from '@/models/PerfectStoreListModel';
import {
  getPerfectStoreList,
  getPerfectStoreCsvFile
} from '@/services/perfectStore';
import {useQueries, keepPreviousData} from '@tanstack/react-query';
import {buildQueryParams} from '@/utils/commons';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {useCallback, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch} from '@/store';
import {resetPerfectStore} from '@/store/slices/perfectStoreSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {downloadPerfectStoreReport} from '@/helpers/perfectStoreUtils';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {PerfectStoreListColConfig} from '@/services/MockData/PerfectStoreList/PerfectStoreListColConfig';
import {ROUTES} from '@/utils/constants/routesConstants';

const initialSortConfig = {
  column: 'startDate',
  direction: 'asc'
};

/**
 * Hook for Perfect Store list data fetching, pagination, sorting, search state,
 * and page actions (create, report download, table columns).
 * @param {boolean} enableQuery - Whether to enable the Perfect Store list query.
 * @param {Function} [cellDataMapperObj] - Builds cellData mappers; pass the one from PerfectStoreList.jsx.
 */
export const usePerfectStoreListQuery = (
  enableQuery = true,
  cellDataMapperObj = null
) => {
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {downloadFromBlobUrl} = useAzureBlob();

  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll
  } = useDataTable(0, false, false, initialSortConfig);

  const queryParams = useMemo(() => {
    return buildQueryParams({
      ...(sortingObj?.column && {
        sortBy: sortingObj.column,
        order: sortingObj.direction
      }),
      pageSize,
      page: pageIndex || 0
    });
  }, [sortingObj?.column, sortingObj?.direction, pageIndex, pageSize]);

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['PERFECT_STORE_TARGETS_LIST', queryParams, searchArr],
        queryFn: () => getPerfectStoreList(queryParams, searchArr),
        enabled: enableQuery,
        placeholderData: keepPreviousData
      }
    ]
  });

  const resultsData = useMemo(() => data || {}, [data]);

  const parsedPerfectStoreData = useMemo(
    () => GetFormattedPerfectStoreData(resultsData),
    [resultsData]
  );

  const {data: perfectStoreData, totalRecords} = useStableListQueryData(
    parsedPerfectStoreData,
    isFetching
  );

  const handleAddPerfectStore = useCallback(() => {
    dispatch(resetPerfectStore());
    navigate(ROUTES.PERFECT_STORE_ADD);
  }, [dispatch, navigate]);

  const onDownloadBtnClick = useCallback(
    async (selectedIds, excludeIds = []) => {
      const body = {
        ids: selectedIds,
        excludeIds,
        filters: searchArr
      };
      const data = await getPerfectStoreCsvFile(queryParams, body);
      const fileName = `PerfectStoreTargets_${getDateWithTimeString()}`;
      downloadCSVFiles(data, fileName);
    },
    [queryParams, searchArr]
  );

  const handleReportDownload = useCallback(
    async (targetId, targetName) => {
      await downloadPerfectStoreReport(targetId, {
        downloadFromBlobUrl,
        targetName,
        onToast: (message, type) =>
          dispatch(setToastNotifications({show: true, message, type}))
      });
    },
    [dispatch, downloadFromBlobUrl]
  );

  // Only built once a cellDataMapperObj is supplied (list page usage).
  const columns = useMemo(
    () =>
      cellDataMapperObj
        ? getCellDataMappedCols(
            cellDataMapperObj(navigate, handleReportDownload),
            PerfectStoreListColConfig
          )
        : [],
    [cellDataMapperObj, navigate, handleReportDownload]
  );

  return {
    perfectStoreData,
    totalRecords,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    setSortingObj,
    sortingObj,
    setSearchArry,
    searchArr,
    isLoading,
    isFetching,
    handleResetAll,
    navigate,
    handleAddPerfectStore,
    onDownloadBtnClick,
    columns
  };
};
